import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit'
import SummaryApi from '../common'
import {
    getGuestCart,
    countGuestCart,
    clearGuestCart,
    removeGuestItemById,
    updateGuestQty,
} from '../utils/guestCart'

// Single source of truth for cart state across the app.
//
// Before this slice existed, cart state was scattered:
//   - App.js owned a `cartProductCount` useState for the authed server count
//   - Header.js owned a separate `guestCartCount` useState for the guest count
//   - guestCart.js dispatched a `guest-cart-updated` CustomEvent which Header listened for
//   - Cart.js called `context.fetchUserAddToCart()` from props to refresh the header badge
//
// All four are now Redux-driven. The slice tracks both server count and guest items so
// the same selector resolves either flow, and consumers just dispatch thunks instead of
// reaching into Context.

// --- Thunks ---

export const fetchServerCartCount = createAsyncThunk(
    'cart/fetchServerCount',
    async (_, { rejectWithValue }) => {
        try {
            const res = await fetch(SummaryApi.addToCartProductCount.url, {
                method: SummaryApi.addToCartProductCount.method,
                credentials: 'include',
            })
            if (res.status === 401) return 0 // not authed yet — expected on first load
            const json = await res.json()
            return Number(json?.data?.count || 0)
        } catch (err) {
            return rejectWithValue({ message: err.message || 'Failed to fetch cart count' })
        }
    },
)

// Bulk-clears every server-side cart item by issuing N delete requests in parallel. The
// API surface doesn't currently have an atomic "empty cart" endpoint; if/when it does,
// this thunk is the single place to swap implementation.
export const clearServerCart = createAsyncThunk(
    'cart/clearServer',
    async (_, { rejectWithValue }) => {
        try {
            const listRes = await fetch(SummaryApi.addToCartProductView.url, {
                method: SummaryApi.addToCartProductView.method,
                credentials: 'include',
            })
            const list = await listRes.json()
            if (!list?.success || !list?.data?.length) {
                return { success: true, message: 'Cart was already empty' }
            }
            await Promise.all(list.data.map((item) => fetch(SummaryApi.deleteCartProduct.url, {
                method: SummaryApi.deleteCartProduct.method,
                credentials: 'include',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ _id: item._id }),
            })))
            return { success: true, message: 'Cart cleared successfully' }
        } catch (err) {
            return rejectWithValue({ message: err.message || 'Failed to clear cart' })
        }
    },
)

const initialState = {
    serverCount: 0, // count from backend for authed users
    guestItems: getGuestCart(), // hydrate from localStorage on init
    status: 'idle',
}

const cartSlice = createSlice({
    name: 'cart',
    initialState,
    reducers: {
        // Reset the authed cart count to 0 (used on logout). Cheaper than a server fetch.
        resetServerCount(state) {
            state.serverCount = 0
        },
        // Replace the guest-items array wholesale. Used by guest add/update/remove
        // helpers — those still own the localStorage write, this just keeps Redux in sync.
        setGuestItems(state, action) {
            state.guestItems = Array.isArray(action.payload) ? action.payload : []
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchServerCartCount.fulfilled, (state, action) => {
                state.serverCount = Number(action.payload || 0)
                state.status = 'succeeded'
            })
            .addCase(fetchServerCartCount.pending, (state) => { state.status = 'loading' })
            .addCase(fetchServerCartCount.rejected, (state) => { state.status = 'failed' })
            .addCase(clearServerCart.fulfilled, (state) => {
                state.serverCount = 0
            })
    },
})

export const { resetServerCount, setGuestItems } = cartSlice.actions

// --- Guest-cart action creators that keep Redux and localStorage in sync ---
//
// These are intentionally thunks rather than reducers so we don't have to fork the
// existing guestCart.js helpers (which other code may import directly). They write to
// localStorage via the helper, then mirror the result into Redux state. The store
// subscription in store.js no longer needs the legacy `guest-cart-updated` CustomEvent.

export const addGuestItemAction = (productSnapshot, quantity = 1, ticketType = null, variantInfo = null) => async (dispatch) => {
    // Lazy import keeps the heavier helper out of the cartSlice's import graph at start.
    const { addGuestItem } = await import('../utils/guestCart')
    const next = await addGuestItem(productSnapshot, quantity, ticketType, variantInfo)
    dispatch(setGuestItems(next))
}

export const updateGuestQtyAction = (itemId, quantity) => (dispatch) => {
    const next = updateGuestQty(itemId, quantity)
    dispatch(setGuestItems(next))
}

export const removeGuestItemAction = (itemId) => (dispatch) => {
    const next = removeGuestItemById(itemId)
    dispatch(setGuestItems(next))
}

export const clearGuestCartAction = () => (dispatch) => {
    clearGuestCart()
    dispatch(setGuestItems([]))
}

// --- Selectors ---

const selectCart = (state) => state.cart || initialState
const selectIsAuthed = (state) => !!state?.user?.user?._id

// Single selector that returns the right count regardless of auth state. Memoized so
// consumers don't re-render when an unrelated cart field changes (e.g. status).
export const selectCartCount = createSelector(
    [selectCart, selectIsAuthed],
    (cart, isAuthed) => {
        if (isAuthed) return Number(cart.serverCount || 0)
        // Compute on the fly from guestItems so we don't keep a derived `guestCount`
        // duplicated in state (which would have to be kept in sync on every mutation).
        return cart.guestItems.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0)
    },
)

// Re-export for tests/back-compat — same shape as the helper.
export { countGuestCart }

export default cartSlice.reducer
